import type { IElement, ITextElement } from './types'
import { walkTree } from './walk'
import { splitParagraphs } from './paragraph'

let pageNumberSequence = 0

/** Word 页码域的数字格式，与编辑器样式保持一致。 */
export const PAGE_NUMBER_FORMATS: Record<string, string> = {
  ARABIC: '1, 2, 3 ...',
  ROMAN: 'I, II, III ...',
  roman: 'i, ii, iii ...',
  ALPHABETIC: 'A, B, C ...',
  alphabetic: 'a, b, c ...',
  CHINESENUM1: '一, 二, 三 ...',
  CHINESENUM2: '壹, 贰, 叁 ...',
  DBNUM1: '一, 二, 三 ...',
  DBNUM2: '壹, 贰, 叁 ...',
  CIRCLE: '①, ②, ③ ...',
  ZODIAC1: '甲, 乙, 丙 ...',
  ZODIAC2: '子, 丑, 寅 ...',
  ORDINAL: '1st, 2nd, 3rd ...',
  CardText: 'One, Two, Three ...',
  OrdText: 'First, Second, Third ...'
}

export interface PageField {
  type: 'PAGE' | 'NUMPAGES' | 'SECTIONPAGES'
  instruction: string
  parent: IElement[]
  start: number
  end: number
  separator: IElement
  results: IElement[]
}

/** 配对复杂域与简单域的统一标记，不把 PAGEREF 或普通数字误认成页码。 */
export function collectPageFields(elements: IElement[]): PageField[] {
  const parents = new Set<IElement[]>([elements])
  walkTree(elements, (_, ctx) => {
    if (Array.isArray(ctx.parent)) parents.add(ctx.parent)
  })
  const fields: PageField[] = []
  for (const parent of parents) {
    const stack: { start: number; instruction: string; separator?: IElement; results: IElement[] }[] = []
    parent.forEach((node, index) => {
      const marker = node.extension?.fieldMarker as { kind?: string; value?: string } | undefined
      if (marker?.kind === 'boundary' && marker.value === 'begin') {
        stack.push({ start: index, instruction: '', results: [] })
        return
      }
      const current = stack[stack.length - 1]
      if (!current) return
      if (marker?.kind === 'instruction') {
        current.instruction += marker.value ?? ''
      } else if (marker?.kind === 'boundary' && marker.value === 'separate') {
        current.separator = node
      } else if (marker?.kind === 'boundary' && marker.value === 'end') {
        stack.pop()
        const type = /^\s*(PAGE|NUMPAGES|SECTIONPAGES)\b/i.exec(current.instruction)?.[1].toUpperCase()
        // 嵌套在 IF 等域中的页码不能脱离外层结果独立求值。
        if (type && current.separator && stack.length === 0) {
          fields.push({ ...current, type: type as PageField['type'], parent, end: index + 1, separator: current.separator })
        }
      } else if (!marker && current.separator && node.type === 'text' && !node.extension?.bookmarkMarker) {
        current.results.push(node)
      }
    })
  }
  return fields
}

/** 从域开关读取数字样式；大小写区分罗马数字和英文字母格式。 */
export function getPageFieldStyle(field: PageField): string {
  const explicit = field.results.find(run => typeof run.extension?.pageNumberStyle === 'string')
  if (explicit) return String(explicit.extension!.pageNumberStyle)
  for (const match of field.instruction.matchAll(/\\\*\s+([A-Za-z0-9]+)/g)) {
    const style = PAGE_NUMBER_FORMATS[match[1]] ?? PAGE_NUMBER_FORMATS[match[1].toUpperCase()]
    if (style) return style
  }
  return '1, 2, 3 ...'
}

/** 生成可导出的真实域，页码不再仅存在于 Canvas 覆盖层。 */
function createPageNumberElements(style: string, template: IElement, position: 'left' | 'center' | 'right'): IElement[] {
  const base = { ...template, type: 'text', rowFlex: position } as ITextElement
  // 把前后缀和总页数视为同一个可更新对象，混排时再次修改也不会叠加。
  const group = `page-number-${Date.now()}-${++pageNumberSequence}`
  base.extension = { pageNumberGroup: group }
  if (base.paragraphFrame) base.paragraphFrame = { ...base.paragraphFrame, xAlign: position }
  base.paragraphFirstLineIndent = 0
  base.paragraphIndentLeft = 0
  base.paragraphIndentRight = 0
  // Java 协议用 paragraphFirstLineIndent 表达悬挂缩进，不发送布局层字段。
  delete (base as ITextElement & { indentHanging?: number }).indentHanging
  const text = (value: string): IElement => ({ ...base, value })
  const field = (type: 'PAGE' | 'NUMPAGES', numberStyle: string): IElement[] => {
    const format = Object.entries(PAGE_NUMBER_FORMATS).find(([, value]) => value === numberStyle)?.[0] ?? 'ARABIC'
    const marker = (kind: string, value: string): IElement => ({
      ...base, value: '', extension: { ...base.extension, fieldMarker: { kind, value } }
    })
    return [
      marker('boundary', 'begin'),
      marker('instruction', ` ${type} \\* ${format} `),
      marker('boundary', 'separate'),
      { ...base, value: '1', extension: { ...base.extension, pageNumberStyle: numberStyle } },
      marker('boundary', 'end')
    ]
  }
  const chinese = style === '第一页' || style === '第一页共X页'
  const numberStyle = chinese ? '一, 二, 三 ...' : '1, 2, 3 ...'
  if (['第1页', '第1页共x页', '第一页', '第一页共X页'].includes(style)) {
    return [
      text('第'), ...field('PAGE', numberStyle), text('页'),
      ...(['第1页共x页', '第一页共X页'].includes(style)
        ? [text('共'), ...field('NUMPAGES', numberStyle), text('页')] : [])
    ]
  }
  if (style === '1/x') return [...field('PAGE', numberStyle), text('/'), ...field('NUMPAGES', numberStyle)]
  if (style === '-1-, -2-, -3- ...' || style === '—1—, —2—, —3— ...') {
    const dash = style.startsWith('-') ? '-' : '—'
    return [text(dash), ...field('PAGE', numberStyle), text(dash)]
  }
  return field('PAGE', style)
}

/** 原位更新当前区域的页码段落；混排段落只替换域，保留业务文字与书签。 */
export function updatePageFields(elements: IElement[], style: string | undefined, position: 'left' | 'center' | 'right'): void {
  const fields = collectPageFields(elements)
  const parents = new Set(fields.filter(field => field.type === 'PAGE').map(field => field.parent))
  for (const parent of parents) {
    for (const group of splitParagraphs(parent).reverse()) {
      if (group.kind !== 'normal') continue
      const pageFields = fields.filter(field => field.parent === parent && field.start >= group.start && field.end <= group.end)
      if (!pageFields.some(field => field.type === 'PAGE')) continue
      for (const run of group.runs) {
        run.rowFlex = position
        if (run.paragraphFrame) run.paragraphFrame = { ...run.paragraphFrame, xAlign: position }
      }
      // 仅改变位置时保留原有前后缀、总页数域和数字格式。
      if (style === undefined) continue
      const insideField = (index: number) => pageFields.some(field => index >= field.start && index < field.end)
      const standalone = group.runs.every((run, index) => !run.extension?.bookmarkMarker && (insideField(group.start + index) ||
        run.type === 'text' && !run.extension?.bookmarkMarker && !run.extension?.fieldMarker &&
        /^(?:[\s\u200B\uFEFF第页共/()（）\-—]|Page|of)*$/i.test(String(run.value ?? ''))))
      const first = pageFields.find(field => field.type === 'PAGE')!
      const template = first.results[0] ?? first.separator
      if (standalone) {
        const last = group.runs[group.runs.length - 1]
        const terminator = last?.type === 'text' && /^[\u200B\uFEFF]+$/.test(last.value) ? [last] : []
        parent.splice(group.start, group.end - group.start, ...createPageNumberElements(style, template, position), ...terminator)
      } else {
        for (const field of pageFields.filter(field => field.type === 'PAGE').sort((a, b) => b.start - a.start)) {
          let start = field.start
          let end = field.end
          const groupId = parent[start].extension?.pageNumberGroup
          if (groupId) {
            while (start > group.start && parent[start - 1].extension?.pageNumberGroup === groupId) start--
            while (end < parent.length && parent[end].extension?.pageNumberGroup === groupId) end++
          }
          const bookmarks = parent.slice(start, end).filter(run => run.extension?.bookmarkMarker)
          const isStart = (run: IElement) => (run.extension?.bookmarkMarker as { position?: string })?.position === 'start'
          parent.splice(start, end - start,
            ...bookmarks.filter(isStart),
            ...createPageNumberElements(style, field.results[0] ?? field.separator, position),
            ...bookmarks.filter(run => !isStart(run)))
        }
      }
    }
  }
  if (parents.size === 0) {
    if (elements.length === 1 && elements[0].type === 'text' && !elements[0].value &&
      !elements[0].extension && !elements[0].paragraphBorder) elements.splice(0, 1)
    const last = elements[elements.length - 1]
    if (elements.length && !(last?.type === 'text' && /^[\u200B\uFEFF]+$/.test(last.value))) {
      elements.push({ type: 'text', value: '\u200B' })
    }
    elements.push(...createPageNumberElements(style ?? '1, 2, 3 ...', { type: 'text', value: '', size: 12 }, position))
  }
}
